import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Search, Calendar, ShieldAlert,
  Filter, X, Globe
} from 'lucide-react';
import { auditLogService } from '../../services';
import { LoadingPage } from '../../components/ui/Loading';
import { formatDate } from '../../utils/dateUtils';

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const METHOD_COLORS = {
  GET: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
  POST: 'text-green-400 bg-green-500/10 border-green-500/20',
  PUT: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  PATCH: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  DELETE: 'text-red-400 bg-red-500/10 border-red-500/20',
};

const statusColor = (code) => {
  if (!code) return 'text-gray-400 bg-gray-500/10 border-gray-500/20';
  if (code >= 500) return 'text-red-400 bg-red-500/10 border-red-500/20';
  if (code >= 400) return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
  if (code >= 200) return 'text-green-400 bg-green-500/10 border-green-500/20';
  return 'text-gray-400 bg-gray-500/10 border-gray-500/20';
};

const AdminAccessLogReport = () => {
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [routeInput, setRouteInput] = useState('');
  const [routeFilter, setRouteFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [statusCodeFilter, setStatusCodeFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 30;

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = { page: currentPage, limit: itemsPerPage };
      if (routeFilter.trim()) params.route = routeFilter.trim();
      if (methodFilter) params.method = methodFilter;
      if (statusCodeFilter.trim()) params.statusCode = statusCodeFilter.trim();
      if (ipFilter.trim()) params.ipAddress = ipFilter.trim();
      if (startDate) params.startDate = `${startDate} 00:00:00`;
      if (endDate) params.endDate = `${endDate} 23:59:59`;

      const data = await auditLogService.getAll(params);
      setLogs(data.logs || []);
      setTotalPages(data.pagination?.pages || 1);
      setTotalItems(data.pagination?.total || 0);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Você não tem permissão para acessar esta página.');
      } else {
        setError(err.response?.data?.error || 'Erro ao carregar log de acesso');
      }
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, routeFilter, methodFilter, statusCodeFilter, ipFilter, startDate, endDate]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleRouteKeyPress = (e) => {
    if (e.key === 'Enter') {
      setRouteFilter(routeInput);
      setCurrentPage(1);
    }
  };

  const setAndResetPage = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };

  const handleMethodChange = setAndResetPage(setMethodFilter);
  const handleStatusCodeChange = setAndResetPage(setStatusCodeFilter);
  const handleIpChange = setAndResetPage(setIpFilter);
  const handleStartDateChange = setAndResetPage(setStartDate);
  const handleEndDateChange = setAndResetPage(setEndDate);

  const clearFilters = () => {
    setRouteInput('');
    setRouteFilter('');
    setMethodFilter('');
    setStatusCodeFilter('');
    setIpFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const hasActiveFilters = routeFilter || methodFilter || statusCodeFilter || ipFilter || startDate || endDate;

  if (loading && logs.length === 0) {
    return <LoadingPage message="Carregando log de acesso..." title="Log de Acesso" />;
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6 overflow-hidden">
      <div className="max-w-full mx-auto flex flex-col gap-4 h-full min-h-0 w-full">

        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-4 sm:p-5 border border-white/10 shadow-xl">
          <div className="flex items-center gap-4 mb-4">
            <button onClick={() => navigate('/app/admin')}
              className="w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-gray-300 hover:text-white transition-all flex items-center justify-center">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent flex items-center gap-3">
                <ShieldAlert className="w-7 h-7 text-cyan-400" />
                Log de Acesso
              </h1>
              <p className="text-gray-400 mt-1 text-sm">
                Requisições feitas à API: quem, quando, rota e status
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <Search className="w-3 h-3" /> Rota
              </label>
              <input type="text" value={routeInput}
                onChange={e => setRouteInput(e.target.value)}
                onKeyPress={handleRouteKeyPress}
                placeholder="/api/events... (Enter para buscar)"
                className="w-full px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <Filter className="w-3 h-3" /> Método
              </label>
              <select value={methodFilter} onChange={e => handleMethodChange(e.target.value)}
                className="px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50">
                <option value="">Todos</option>
                {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Status</label>
              <input type="number" value={statusCodeFilter}
                onChange={e => handleStatusCodeChange(e.target.value)}
                placeholder="Ex: 500"
                className="w-24 px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <Globe className="w-3 h-3" /> IP
              </label>
              <input type="text" value={ipFilter}
                onChange={e => handleIpChange(e.target.value)}
                placeholder="192.168.0.1"
                className="w-32 px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> De
              </label>
              <input type="date" value={startDate} onChange={e => handleStartDateChange(e.target.value)}
                className="px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Até
              </label>
              <input type="date" value={endDate} onChange={e => handleEndDateChange(e.target.value)}
                className="px-3 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
            {hasActiveFilters && (
              <button onClick={clearFilters}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10 transition-all">
                <X className="w-4 h-4" /> Limpar
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="flex-shrink-0 bg-red-900/20 rounded-xl p-4 border border-red-500/20 text-red-400 text-center">{error}</div>
        )}

        {/* Table */}
        <div className="flex-1 min-h-0 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl overflow-hidden flex flex-col">
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm" style={{ minWidth: '900px' }}>
              <thead className="sticky top-0 z-20">
                <tr className="border-b border-white/10 bg-slate-800/95">
                  <th className="text-left px-4 py-3 text-gray-400 font-medium sticky left-0 bg-slate-800/95 z-30">Usuário</th>
                  <th className="text-center px-3 py-3 text-gray-400 font-medium">Método</th>
                  <th className="text-left px-3 py-3 text-gray-400 font-medium">Rota</th>
                  <th className="text-left px-3 py-3 text-gray-400 font-medium whitespace-nowrap">IP</th>
                  <th className="text-center px-3 py-3 text-gray-400 font-medium whitespace-nowrap">Status</th>
                  <th className="text-right px-3 py-3 text-gray-400 font-medium whitespace-nowrap">Tempo</th>
                  <th className="text-left px-3 py-3 text-gray-400 font-medium whitespace-nowrap">Data</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-16 text-gray-400">
                      <ShieldAlert className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Nenhum registro encontrado</p>
                    </td>
                  </tr>
                ) : logs.map((log) => (
                  <tr key={log.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3 sticky left-0 bg-slate-800/95 z-10">
                      {log.user ? (
                        <div>
                          <div className="text-white font-medium whitespace-nowrap">{log.user.name}</div>
                          <div className="text-xs text-gray-500 whitespace-nowrap">{log.user.email}</div>
                        </div>
                      ) : (
                        <span className="text-gray-500 text-xs">Anônimo</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-block px-2 py-1 rounded-lg text-xs font-bold border ${METHOD_COLORS[log.method] || 'text-gray-400 bg-gray-500/10 border-gray-500/20'}`}>
                        {log.method}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-gray-300 font-mono text-xs max-w-[320px] truncate" title={log.route}>{log.route}</td>
                    <td className="px-3 py-3 text-gray-400 text-xs whitespace-nowrap">{log.ip_address || '—'}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-block px-2 py-1 rounded-lg text-xs font-mono font-bold border ${statusColor(log.status_code)}`}>
                        {log.status_code ?? '—'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right text-gray-400 font-mono text-xs whitespace-nowrap">
                      {log.response_time_ms != null ? `${log.response_time_ms}ms` : '—'}
                    </td>
                    <td className="px-3 py-3 text-gray-400 text-xs whitespace-nowrap">
                      {log.created_at ? formatDate(log.created_at) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex-shrink-0 px-4 py-3 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-gray-500">
              {totalItems > 0
                ? `${(currentPage - 1) * itemsPerPage + 1}–${Math.min(currentPage * itemsPerPage, totalItems)} de ${totalItems} registro(s)`
                : '0 registros'}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1 || loading}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-300 px-2">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages || loading}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAccessLogReport;
